import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { useAuth } from "../../../hooks/useAuth";
import {
  Info,
  AlertCircle,
  User,
  Building2,
  BarChart3,
  FileText,
  Calendar,
  ArrowRight,
  DollarSign,
  ClipboardCheck,
} from "lucide-react";
import {
  proponenteService,
  type StatusDocumentosResponse,
  type DocumentoStatus,
} from "../../../services/proponenteService";
import editaisPublicosService, {
  type EditalPublico,
} from "../../../services/editaisPublicosService";

export default function HomeProponente() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [status, setStatus] = useState<StatusDocumentosResponse | null>(null);
  const [editais, setEditais] = useState<EditalPublico[]>([]);
  const [loading, setLoading] = useState(true);
  const [reenvioModal, setReenvioModal] = useState<{
    tipo: "rg_cpf" | "formacao" | "residencia";
    nome: string;
  } | null>(null);
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    carregarDados();
  }, []);

  const carregarDados = async () => {
    try {
      const [statusData, editaisData] = await Promise.all([
        proponenteService.getStatusDocumentos(),
        editaisPublicosService.listar(),
      ]);
      setStatus(statusData);
      setEditais(editaisData.editais || []);
    } catch (error) {
      console.error("Erro ao carregar dados:", error);
    } finally {
      setLoading(false);
    }
  };

  const carregarStatus = async () => {
    try {
      const data = await proponenteService.getStatusDocumentos();
      setStatus(data);
    } catch (error) {
      console.error("Erro ao carregar status:", error);
    }
  };

  const handleReenviar = async () => {
    if (!reenvioModal || !arquivo) return;

    setEnviando(true);
    try {
      await proponenteService.reenviarDocumento({
        documentoTipo: reenvioModal.tipo,
        arquivo,
      });
      toast.success("Documento reenviado com sucesso! Aguarde a análise.");
      setReenvioModal(null);
      setArquivo(null);
      carregarStatus();
    } catch (error) {
      console.error("Erro ao reenviar:", error);
      toast.error("Erro ao reenviar documento. Tente novamente.");
    } finally {
      setEnviando(false);
    }
  };

  const getNomeDocumento = (tipo: string) => {
    switch (tipo) {
      case "rg_cpf":
        return "RG/CPF";
      case "formacao":
        return "Comprovante de Formação";
      case "residencia":
        return "Comprovante de Residência";
      default:
        return tipo;
    }
  };

  const getStatusBadge = (doc: DocumentoStatus) => {
    const statusMap = {
      aprovado: {
        bg: "bg-gradient-to-r from-green-100 to-green-200",
        text: "text-green-800",
        label: "Aprovado",
        icon: (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
        ),
      },
      pendente: {
        bg: "bg-gradient-to-r from-yellow-100 to-yellow-200",
        text: "text-yellow-800",
        label: "Em análise",
        icon: (
          <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        ),
      },
      reprovado: {
        bg: "bg-gradient-to-r from-red-100 to-red-200",
        text: "text-red-800",
        label: "Reprovado",
        icon: (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
        ),
      },
      nao_enviado: {
        bg: "bg-gradient-to-r from-gray-100 to-gray-200",
        text: "text-gray-800",
        label: "Não enviado",
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        ),
      },
    };
    const config = statusMap[doc.status] || statusMap.nao_enviado;
    return (
      <span
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs sm:text-sm font-semibold ${config.bg} ${config.text} shadow-sm`}
      >
        {config.icon}
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent mb-4"></div>
          <p className="text-gray-600 font-medium">Carregando seus dados...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-gray-50">
      <main className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
        {/* Banner de status */}
        {status?.status.pendentes && !status.status.algum_reprovado && (
          <div className="bg-gradient-to-r from-blue-50 to-blue-100/50 border border-blue-200 rounded-xl p-4 sm:p-5 mb-6 shadow-sm hover:shadow-md transition-shadow animate-fadeIn">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                  <Info className="w-5 h-5 text-white" />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm sm:text-base font-semibold text-blue-900 mb-1">
                  Seus documentos estão em análise
                </h3>
                <p className="text-xs sm:text-sm text-blue-700">
                  Aguarde a validação pela equipe administrativa. Você será notificado assim que a análise for concluída.
                </p>
              </div>
            </div>
          </div>
        )}

        {status?.status.algum_reprovado && (
          <div className="bg-gradient-to-r from-red-50 to-red-100/50 border border-red-200 rounded-xl p-4 sm:p-5 mb-6 shadow-sm hover:shadow-md transition-shadow animate-fadeIn">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center">
                    <AlertCircle className="w-5 h-5 text-white" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm sm:text-base font-semibold text-red-900 mb-1">
                    Documentos recusados
                  </h3>
                  <p className="text-xs sm:text-sm text-red-700">
                    Um ou mais documentos foram recusados. Faça o reenvio para continuar o processo.
                  </p>
                </div>
              </div>
              <button
                onClick={() =>
                  window.scrollTo({
                    top: document.body.scrollHeight,
                    behavior: "smooth",
                  })
                }
                className="w-full sm:w-auto px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 active:bg-red-800 transition-all text-sm font-semibold shadow-sm hover:shadow-md whitespace-nowrap"
              >
                Reenviar agora
              </button>
            </div>
          </div>
        )}

        {status?.status.todos_aprovados && (
          <>
            <div className="mb-8">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1">
                    Editais Disponíveis
                  </h2>
                  <p className="text-sm text-gray-600">
                    Confira as chamadas vigentes e inscreva-se
                  </p>
                </div>
              </div>

              {editais.length === 0 ? (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 sm:p-12 text-center">
                  <div className="w-16 h-16 bg-gray-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                    <FileText className="w-8 h-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Nenhum edital disponível
                  </h3>
                  <p className="text-gray-600 max-w-md mx-auto">
                    Não há editais vigentes no momento. Volte em breve para conferir novas oportunidades.
                  </p>
                </div>
              ) : (
                <div className="grid gap-4 sm:gap-5">
                  {editais.slice(0, 5).map((edital) => (
                    <div
                      key={edital.id}
                      className="bg-white rounded-lg shadow-sm hover:shadow-md border border-gray-200 transition-shadow"
                    >
                      <div className="p-4 sm:p-5">
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-2 line-clamp-2">
                              {edital.nome}
                            </h3>
                            <div className="flex flex-wrap items-center gap-2 text-xs sm:text-sm text-gray-600">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-4 h-4" />
                                <span className="text-gray-500">Término:</span>
                                <span className="font-medium">
                                  {new Date(edital.data_fim).toLocaleDateString("pt-BR")}
                                </span>
                              </span>
                            </div>
                          </div>
                          <button
                            onClick={() => navigate(`/editais/${edital.id}`)}
                            className="w-full sm:w-auto bg-blue-600 text-white px-5 py-2.5 rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm whitespace-nowrap"
                          >
                            Ver Detalhes
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Botão Ver Todos os Editais */}
              {editais.length > 5 && (
                <div className="mt-6 text-center">
                  <button
                    onClick={() => navigate("/editais")}
                    className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
                  >
                    Ver Todos os Editais ({editais.length})
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>

            {/* Cards de Ações */}
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6">
                Ações Rápidas
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 mb-8">
                {/* Perfil do Proponente */}
                <div className="bg-white rounded-xl shadow-sm hover:shadow-lg border border-gray-200 hover:border-blue-300 p-5 sm:p-6 text-center transition-all duration-300 group">
                  <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl mx-auto mb-4 flex items-center justify-center group-hover:from-blue-100 group-hover:to-blue-200 transition-all duration-300 group-hover:scale-110">
                    <User className="w-7 h-7 sm:w-8 sm:h-8 text-gray-600 group-hover:text-blue-600 transition-colors" />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-1 text-sm sm:text-base truncate px-2">
                    {user?.nome || "Proponente"}
                  </h3>
                  <p className="text-xs text-gray-500 mb-4">Perfil do Usuário</p>
                  <button className="bg-gradient-to-r from-blue-900 to-blue-800 text-white px-4 py-2.5 rounded-lg text-xs sm:text-sm font-semibold hover:from-blue-800 hover:to-blue-700 active:scale-95 transition-all w-full shadow-sm hover:shadow-md">
                    Atualizar Perfil
                  </button>
                </div>

                {/* Dados da Empresa */}
                <div className="bg-white rounded-xl shadow-sm hover:shadow-lg border border-gray-200 hover:border-blue-300 p-5 sm:p-6 text-center transition-all duration-300 group">
                  <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br from-blue-100 to-blue-200 rounded-xl mx-auto mb-4 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <Building2 className="w-7 h-7 sm:w-8 sm:h-8 text-blue-600" />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-1 text-sm sm:text-base">
                    Dados da Empresa
                  </h3>
                  <p className="text-xs text-gray-500 mb-4 line-clamp-2">
                    Preencha os dados da sua empresa
                  </p>
                  <button className="bg-gradient-to-r from-blue-900 to-blue-800 text-white px-4 py-2.5 rounded-lg text-xs sm:text-sm font-semibold hover:from-blue-800 hover:to-blue-700 active:scale-95 transition-all w-full shadow-sm hover:shadow-md">
                    Atualizar Dados
                  </button>
                </div>

                {/* Avaliar Projetos */}
                <div className="bg-white rounded-xl shadow-sm hover:shadow-lg border border-gray-200 hover:border-blue-300 p-5 sm:p-6 text-center transition-all duration-300 group">
                  <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br from-purple-100 to-purple-200 rounded-xl mx-auto mb-4 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <BarChart3 className="w-7 h-7 sm:w-8 sm:h-8 text-purple-600" />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-1 text-sm sm:text-base">
                    Avaliar Projetos
                  </h3>
                  <p className="text-xs text-gray-500 mb-4 line-clamp-2">
                    Projetos cadastrados no sistema
                  </p>
                  <button className="bg-gradient-to-r from-blue-900 to-blue-800 text-white px-4 py-2.5 rounded-lg text-xs sm:text-sm font-semibold hover:from-blue-800 hover:to-blue-700 active:scale-95 transition-all w-full shadow-sm hover:shadow-md">
                    Avaliar Agora
                  </button>
                </div>

                {/* Relatório Técnico */}
                <div className="bg-white rounded-xl shadow-sm hover:shadow-lg border border-gray-200 hover:border-blue-300 p-5 sm:p-6 text-center transition-all duration-300 group">
                  <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br from-green-100 to-green-200 rounded-xl mx-auto mb-4 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <FileText className="w-7 h-7 sm:w-8 sm:h-8 text-green-600" />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-1 text-sm sm:text-base">
                    Relatório Técnico
                  </h3>
                  <p className="text-xs text-gray-500 mb-4 line-clamp-2">
                    Apresente relatórios do projeto
                  </p>
                  <button className="bg-gradient-to-r from-blue-900 to-blue-800 text-white px-4 py-2.5 rounded-lg text-xs sm:text-sm font-semibold hover:from-blue-800 hover:to-blue-700 active:scale-95 transition-all w-full shadow-sm hover:shadow-md">
                    Enviar Relatório
                  </button>
                </div>

                {/* Prestações de Contas */}
                <div className="bg-white rounded-xl shadow-sm hover:shadow-lg border border-gray-200 hover:border-blue-300 p-5 sm:p-6 text-center transition-all duration-300 group">
                  <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br from-yellow-100 to-yellow-200 rounded-xl mx-auto mb-4 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <DollarSign className="w-7 h-7 sm:w-8 sm:h-8 text-yellow-600" />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-1 text-sm sm:text-base line-clamp-2">
                    Prestações de Contas
                  </h3>
                  <p className="text-xs text-gray-500 mb-4 line-clamp-2">
                    Prestação de contas financeira
                  </p>
                  <button className="bg-gradient-to-r from-blue-900 to-blue-800 text-white px-4 py-2.5 rounded-lg text-xs sm:text-sm font-semibold hover:from-blue-800 hover:to-blue-700 active:scale-95 transition-all w-full shadow-sm hover:shadow-md">
                    Enviar Contas
                  </button>
                </div>

                {/* Acompanhamento */}
                <div className="bg-white rounded-xl shadow-sm hover:shadow-lg border border-gray-200 hover:border-blue-300 p-5 sm:p-6 text-center transition-all duration-300 group">
                  <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br from-indigo-100 to-indigo-200 rounded-xl mx-auto mb-4 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <ClipboardCheck className="w-7 h-7 sm:w-8 sm:h-8 text-indigo-600" />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-1 text-sm sm:text-base line-clamp-2">
                    Acompanhamento
                  </h3>
                  <p className="text-xs text-gray-500 mb-4 line-clamp-2">
                    Projetos em andamento
                  </p>
                  <button className="bg-gradient-to-r from-blue-900 to-blue-800 text-white px-4 py-2.5 rounded-lg text-xs sm:text-sm font-semibold hover:from-blue-800 hover:to-blue-700 active:scale-95 transition-all w-full shadow-sm hover:shadow-md">
                    Acompanhar
                  </button>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Lista de documentos - só mostra se NÃO estiver todos aprovados */}
        {!status?.status.todos_aprovados && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 sm:p-6 lg:p-8">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6">
              Status dos Documentos
            </h2>
            <div className="space-y-4">
              {status?.status.documentos &&
                Object.entries(status.status.documentos).map(([tipo, doc]) => (
                  <div
                    key={tipo}
                    className="border border-gray-200 rounded-xl p-4 sm:p-5 hover:border-gray-300 hover:shadow-md transition-all"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
                      <h3 className="font-semibold text-gray-900 text-sm sm:text-base">
                        {getNomeDocumento(tipo)}
                      </h3>
                      {getStatusBadge(doc)}
                    </div>
                    {doc.aprovacao?.observacoes && (
                      <div className="mt-3 p-4 bg-gradient-to-r from-amber-50 to-amber-100/50 border border-amber-200 rounded-lg text-sm text-gray-700">
                        <div className="flex items-start gap-2">
                          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                          <div>
                            <strong className="font-semibold text-amber-900">Observações:</strong>
                            <p className="mt-1">{doc.aprovacao.observacoes}</p>
                          </div>
                        </div>
                      </div>
                    )}
                    {doc.status === "reprovado" && (
                      <button
                        onClick={() =>
                          setReenvioModal({
                            tipo: tipo as "rg_cpf" | "formacao" | "residencia",
                            nome: getNomeDocumento(tipo),
                          })
                        }
                        className="mt-4 w-full sm:w-auto px-6 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 active:scale-95 transition-all text-sm font-semibold shadow-sm hover:shadow-md"
                      >
                        Reenviar Documento
                      </button>
                    )}
                  </div>
                ))}
            </div>
          </div>
        )}
      </main>

      {/* Modal de reenvio */}
      {reenvioModal && (
        <div
          className="fixed inset-0 flex items-center justify-center p-4 z-50 animate-fadeIn"
          style={{
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            backdropFilter: "blur(4px)",
          }}
          onClick={() => {
            setReenvioModal(null);
            setArquivo(null);
          }}
        >
          <div
            className="bg-white rounded-2xl p-6 sm:p-8 max-w-md w-full shadow-2xl animate-scaleIn"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900">
                  Reenviar Documento
                </h3>
                <p className="text-sm text-gray-600">{reenvioModal.nome}</p>
              </div>
            </div>
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Selecione o arquivo
              </label>
              <div className="relative">
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => setArquivo(e.target.files?.[0] || null)}
                  className="block w-full text-sm text-gray-900 border-2 border-gray-300 rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 focus:outline-none focus:border-blue-500 transition-all p-3 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                />
              </div>
              {arquivo && (
                <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
                  <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-sm font-medium text-green-800 truncate">{arquivo.name}</span>
                </div>
              )}
              <p className="mt-2 text-xs text-gray-500">
                Formatos aceitos: PDF, JPG, JPEG, PNG
              </p>
            </div>
            
            <div className="flex flex-col-reverse sm:flex-row gap-3">
              <button
                onClick={() => {
                  setReenvioModal(null);
                  setArquivo(null);
                }}
                className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 active:scale-95 transition-all font-semibold"
                disabled={enviando}
              >
                Cancelar
              </button>
              <button
                onClick={handleReenviar}
                disabled={!arquivo || enviando}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-semibold shadow-md hover:shadow-lg"
              >
                {enviando ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Enviando...
                  </span>
                ) : (
                  "Enviar Documento"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
